import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, DollarSign, Receipt, Calendar, FileText, Mail, Printer, Loader2, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const toDateStr = (d) => d.toISOString().split('T')[0];
const now = new Date();
const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

export default function SalesTaxReport() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(toDateStr(firstOfMonth));
  const [toDate, setToDate] = useState(toDateStr(now));
  const [showEmail, setShowEmail] = useState(false);
  const [emailAddr, setEmailAddr] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [drillDown, setDrillDown] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [includeOrders, setIncludeOrders] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Load saved reports on mount
  React.useEffect(() => {
    loadSavedReports();
  }, []);

  const loadSavedReports = async () => {
    try {
      const data = await api.request('/api/reports/saved-tax-reports');
      setSavedReports(data);
    } catch (e) { console.error('Failed to load saved tax reports:', e); } finally { setLoadingSaved(false); }
  }; // 'all' | 'taxable' | 'non_taxable' | null

  const handleGenerate = async () => {
    setLoading(true);
    setDrillDown(null);
    try {
      const data = await api.getSalesTaxReport(fromDate, toDate);
      setReport(data);
      loadSavedReports();
    } catch (err) {
      alert('Failed to generate report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async () => {
    if (!emailAddr.trim()) { alert('Enter an email address'); return; }
    setEmailing(true);
    setEmailSent(false);
    try {
      // Use the new dedicated sales-tax email endpoint that handles include_orders + admin restriction
      await api.request('/api/reports/sales-tax/email', {
        method: 'POST',
        body: JSON.stringify({
          to_email: emailAddr.trim(),
          from_date: fromDate,
          to_date: toDate,
          include_orders: includeOrders,
        }),
      });
      setEmailSent(true);
      setTimeout(() => { setShowEmail(false); setEmailSent(false); }, 3000);
    } catch (err) {
      alert('Failed to send email: ' + err.message);
    } finally {
      setEmailing(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${API_URL}/api/reports/sales-tax/pdf?from_date=${fromDate}&to_date=${toDate}&include_orders=${includeOrders}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dlUrl;
      a.download = `sales-tax-${fromDate}-to-${toDate}${includeOrders ? '-with-orders' : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenSaved = async (sr) => {
    setLoading(true);
    setDrillDown(null);
    try {
      // Re-generate the report for the saved date range to get full order details
      const data = await api.getSalesTaxReport(sr.from_date.split('T')[0], sr.to_date.split('T')[0], false);
      setReport(data);
      setFromDate(sr.from_date.split('T')[0]);
      setToDate(sr.to_date.split('T')[0]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert('Failed to load report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSaved = async (id) => {
    if (!window.confirm('Delete this saved report?')) return;
    try {
      await api.request(`/api/reports/saved-tax-reports/${id}`, { method: 'DELETE' });
      setSavedReports(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const setPreset = (preset) => {
    const today = new Date();
    let start;
    if (preset === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
    } else if (preset === 'last_month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      today.setDate(0);
    } else if (preset === 'this_quarter') {
      const q = Math.floor(today.getMonth() / 3) * 3;
      start = new Date(today.getFullYear(), q, 1);
    } else if (preset === 'this_year') {
      start = new Date(today.getFullYear(), 0, 1);
    }
    setFromDate(toDateStr(start));
    setToDate(toDateStr(preset === 'last_month' ? today : new Date()));
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="sales-tax-report">
      {/* Controls — hidden when printing */}
      <div className="no-print">
        <div className="mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="h-6 w-6 text-primary" /> Sales Tax Report
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Illinois Tax Rate: 7.25% — Select a date range and generate</p>
        </div>

        <div className="bg-card border rounded-xl p-5 mb-6" data-testid="date-range-section">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">From Date</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-background" data-testid="from-date" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">To Date</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-background" data-testid="to-date" />
            </div>
            <button onClick={handleGenerate} disabled={loading}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
              data-testid="generate-report-btn">
              <FileText className="h-4 w-4" />
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
            {report && (
              <>
                <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-muted" data-testid="print-tax-report-btn">
                  <Printer className="h-3.5 w-3.5" /> Print
                </button>
                <button onClick={handleDownloadPDF} disabled={downloading} className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-muted disabled:opacity-50" data-testid="download-tax-report-btn">
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  {downloading ? 'Generating...' : 'Download PDF'}
                </button>
                <button onClick={() => setShowEmail(!showEmail)} className="flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md hover:bg-muted" data-testid="email-tax-report-btn">
                  <Mail className="h-3.5 w-3.5" /> Email
                </button>
              </>
            )}
          </div>

          {/* Include orders checkbox */}
          {report && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={includeOrders} onChange={(e) => setIncludeOrders(e.target.checked)}
                  className="mt-0.5" data-testid="include-orders-checkbox"
                />
                <div className="text-sm">
                  <div className="font-medium text-blue-900">Include all sales orders in this report</div>
                  <div className="text-xs text-blue-700 mt-0.5">
                    Bundles every sales order from this period (with line-item detail) into the printed/downloaded/emailed PDF.
                    {includeOrders && <span className="block text-orange-700 mt-1">⚠️ Bundled reports may only be emailed to the admin (pwebb@pwebboil.com).</span>}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Email form */}
          {showEmail && (
            <div className="mt-3 flex items-center gap-2" data-testid="email-form">
              <input type="email" value={emailAddr} onChange={e => setEmailAddr(e.target.value)}
                placeholder="Enter email address"
                className="flex-1 px-3 py-2 border rounded-lg text-sm bg-background" data-testid="email-input" />
              <button onClick={handleEmail} disabled={emailing}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50" data-testid="send-email-btn">
                {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : emailSent ? <CheckCircle className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
                {emailing ? 'Sending...' : emailSent ? 'Sent!' : 'Send'}
              </button>
            </div>
          )}

          {/* Quick Presets */}
          <div className="flex gap-2 mt-3">
            {[
              { key: 'this_month', label: 'This Month' },
              { key: 'last_month', label: 'Last Month' },
              { key: 'this_quarter', label: 'This Quarter' },
              { key: 'this_year', label: 'This Year' },
            ].map(p => (
              <button key={p.key} onClick={() => setPreset(p.key)}
                className="text-xs px-3 py-1 border rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                data-testid={`preset-${p.key}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Report content — visible on screen AND when printing */}
      {report ? (
        <div className="space-y-6" id="tax-report-content">
          {/* Print header — only shows when printing */}
          <div className="print-only" style={{ display: 'none' }}>
            <h1 style={{ fontSize: '18pt', fontWeight: 'bold', marginBottom: '4px' }}>Sales Tax Report</h1>
            <p style={{ fontSize: '10pt', color: '#666' }}>
              Webb Supply — Period: {new Date(fromDate).toLocaleDateString()} to {new Date(toDate).toLocaleDateString()}
            </p>
            <p style={{ fontSize: '9pt', color: '#666' }}>Illinois Tax Rate: 7.25%</p>
            <hr style={{ margin: '8px 0', borderColor: '#333' }} />
          </div>

          {/* Summary Cards — clickable to drill down */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { activeId: 'card-total', drillKey: 'all', label: 'Total Sales', value: fmtCurrency(report.total_sales), icon: DollarSign, color: 'text-green-600', orders: report.all_orders },
              { activeId: 'card-taxable', drillKey: 'taxable', label: 'Taxable Sales', value: fmtCurrency(report.taxable_sales), icon: Receipt, color: 'text-blue-600', orders: report.taxable_orders },
              { activeId: 'card-nontax', drillKey: 'non_taxable', label: 'Non-Taxable Sales', value: fmtCurrency(report.non_taxable_sales), icon: Receipt, color: 'text-gray-500', orders: report.non_taxable_orders },
              { activeId: 'card-orders', drillKey: 'all', label: 'Orders', value: report.order_count, icon: Receipt, color: 'text-foreground', orders: report.all_orders },
            ].map(c => {
              const isActive = activeCard === c.activeId;
              return (
                <div key={c.activeId}
                  onClick={() => {
                    if (isActive) {
                      setActiveCard(null);
                      setDrillDown(null);
                    } else {
                      setActiveCard(c.activeId);
                      setDrillDown(c.drillKey);
                    }
                  }}
                  className={`bg-card border rounded-xl p-4 transition-colors cursor-pointer hover:border-primary/50 ${isActive ? 'border-primary ring-1 ring-primary/20' : ''}`}
                  data-testid={`tax-card-${c.activeId}`}>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                    <span className="flex items-center gap-2"><c.icon className="h-4 w-4" /> {c.label}</span>
                    {isActive ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </div>
                  <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
                  {c.orders && <p className="text-xs text-muted-foreground mt-1">{c.orders.length} order(s) — click to view</p>}
                </div>
              );
            })}
          </div>

          {/* Drill-down order table */}
          {drillDown && report[`${drillDown === 'all' ? 'all' : drillDown}_orders`] && (
            <div className="bg-card border rounded-xl overflow-hidden" data-testid="drill-down-table">
              <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {drillDown === 'all' ? 'All Orders' : drillDown === 'taxable' ? 'Taxable Orders' : 'Non-Taxable Orders'}
                  <span className="text-muted-foreground font-normal ml-2">({report[`${drillDown === 'all' ? 'all' : drillDown}_orders`].length})</span>
                </h3>
                <button onClick={() => { setDrillDown(null); setActiveCard(null); }} className="text-xs text-muted-foreground hover:text-foreground">Close</button>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/10">
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Order #</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Customer</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Taxable</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Non-Taxable</th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {report[`${drillDown === 'all' ? 'all' : drillDown}_orders`].map((o) => (
                    <tr key={o.id}
                      onClick={() => navigate(`/sales-orders/${o.id}`)}
                      className="hover:bg-muted/30 cursor-pointer transition-colors"
                      data-testid={`drill-order-${o.order_number}`}>
                      <td className="px-4 py-2 font-medium text-primary">{o.order_number}</td>
                      <td className="px-4 py-2 text-muted-foreground">{o.date}</td>
                      <td className="px-4 py-2">{o.customer}</td>
                      <td className="px-4 py-2 text-right">{fmtCurrency(o.taxable_amount)}</td>
                      <td className="px-4 py-2 text-right">{fmtCurrency(o.non_taxable_amount)}</td>
                      <td className="px-4 py-2 text-right font-medium">{fmtCurrency(o.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Tax Owed Card */}
          <div className="bg-card border-2 border-primary/30 rounded-xl p-6" data-testid="tax-owed-card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calculator className="h-5 w-5 text-primary" /> Tax Calculation
            </h2>
            <div className="space-y-3 text-sm max-w-md">
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Taxable Sales Subtotal</span>
                <span className="font-medium">{fmtCurrency(report.taxable_sales)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Tax Rate</span>
                <span className="font-medium">{(report.tax_rate * 100).toFixed(2)}%</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Tax Collected (from orders)</span>
                <span className="font-medium">{fmtCurrency(report.total_tax_collected)}</span>
              </div>
              <div className="flex justify-between py-3 bg-primary/5 rounded-lg px-3 mt-2">
                <span className="font-semibold text-foreground">Calculated Tax Owed</span>
                <span className="font-bold text-lg text-primary" data-testid="tax-owed">{fmtCurrency(report.calculated_tax_owed)}</span>
              </div>
            </div>
          </div>

          {/* Return Credits */}
          {report.return_count > 0 && (
            <div className="bg-card border rounded-xl p-4">
              <h3 className="text-sm font-semibold mb-2">Return Credits</h3>
              <div className="flex gap-6 text-sm">
                <span>Returns: {report.return_count}</span>
                <span>Amount: {fmtCurrency(report.total_return_subtotal)}</span>
                <span>Tax Returned: {fmtCurrency(report.total_return_tax)}</span>
              </div>
            </div>
          )}

          {/* Period Info */}
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            Period: {new Date(report.from_date).toLocaleDateString()} — {new Date(report.to_date).toLocaleDateString()}
          </div>

          {/* Print button (no-print) */}
          <div className="text-center no-print">
            <button onClick={handlePrint}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
              data-testid="print-report-btn">
              Print Report
            </button>
          </div>
        </div>
      ) : !loading ? (
        <div className="text-center py-16 text-muted-foreground no-print">
          <Calculator className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p>Select a date range and click "Generate Report"</p>
        </div>
      ) : null}

      {/* Print styles for this page */}
      <style>{`
        .print-only { display: none !important; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          #tax-report-content {
            padding: 0;
            max-width: 100%;
          }
        }
      `}</style>

      {/* Saved Reports History */}
      <div className="mt-8 no-print" data-testid="saved-reports-section">
        <h2 className="text-lg font-bold mb-4">Report History</h2>
        {loadingSaved ? (
          <p className="text-sm text-muted-foreground">Loading saved reports...</p>
        ) : savedReports.length === 0 ? (
          <p className="text-sm text-muted-foreground">No saved reports yet. Generate a report to save it here.</p>
        ) : (
          <div className="bg-card border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Period</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Generated</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">By</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Total Sales</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Tax Owed</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Orders</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {savedReports.map((sr) => (
                  <tr key={sr.id}
                    onClick={() => handleOpenSaved(sr)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors group"
                    title="Click anywhere on this row to open this report"
                    data-testid={`saved-report-${sr.id}`}>
                    <td className="px-4 py-2.5 font-medium text-primary group-hover:underline">
                      {new Date(sr.from_date).toLocaleDateString()} — {new Date(sr.to_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {new Date(sr.generated_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{sr.generated_by}</td>
                    <td className="px-4 py-2.5 text-right">{fmtCurrency(sr.total_sales)}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-primary">{fmtCurrency(sr.calculated_tax_owed)}</td>
                    <td className="px-4 py-2.5 text-right">{sr.order_count}</td>
                    <td className="px-4 py-2.5 text-center">
                      <div className="inline-flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenSaved(sr); }}
                          className="text-xs px-2.5 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 font-medium"
                          data-testid={`open-report-${sr.id}`}
                        >
                          Open
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSaved(sr.id); }}
                          className="text-xs px-2.5 py-1 text-red-500 hover:bg-red-50 rounded hover:text-red-700" data-testid={`delete-report-${sr.id}`}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
